import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

function MobileFileCard({ file, selected, onSelect }) {
  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(file)}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: "pointer",
        borderColor: selected ? "primary.main" : "divider",
      }}
    >
      <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-word", mb: 1 }}>
        {file.path}
      </Typography>
      <Stack spacing={0.75}>
        <Typography variant="caption" color="text.secondary">
          {file.description || "(no description)"}
        </Typography>
        <Typography variant="caption">Type: {file.file_type || ""}</Typography>
        <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
          Tables: {(file.db_tables_used || []).join(", ") || "None recorded"}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function ModuleDetails({ moduleName, selectedProjectId = "", isMobile: forcedMobile = false }) {
  const theme = useTheme();
  const responsiveMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = forcedMobile || responsiveMobile;
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [functions, setFunctions] = useState([]);
  const [functionsLoading, setFunctionsLoading] = useState(false);
  const [functionsError, setFunctionsError] = useState(null);

  useEffect(() => {
    if (!moduleName) {
      setDetails(null);
      setError(null);
      setSelectedFile(null);
      setFunctions([]);
      setFunctionsError(null);
      return;
    }

    const loadDetails = async () => {
      setLoading(true);
      setError(null);
      setDetails(null);
      setSelectedFile(null);
      setFunctions([]);
      setFunctionsError(null);

      try {
        const params = new URLSearchParams();
        if (selectedProjectId) {
          params.set("project_id", selectedProjectId);
        }
        const res = await fetch(`/api/docs/modules/${encodeURIComponent(moduleName)}${params.toString() ? `?${params.toString()}` : ""}`);
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.error || "Failed to load module details.");
        }
        setDetails(data.details || null);
      } catch (err) {
        setError(err.message);
        setDetails(null);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [moduleName, selectedProjectId]);

  useEffect(() => {
    if (!selectedFile || !selectedFile.file_id) {
      setFunctions([]);
      setFunctionsError(null);
      return;
    }

    const loadFunctions = async () => {
      setFunctionsLoading(true);
      setFunctionsError(null);

      try {
        const res = await fetch(`/api/docs/files/${encodeURIComponent(selectedFile.file_id)}/functions`);
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.error || "Failed to load functions.");
        }
        setFunctions(data.functions || []);
      } catch (err) {
        setFunctionsError(err.message);
        setFunctions([]);
      } finally {
        setFunctionsLoading(false);
      }
    };

    loadFunctions();
  }, [selectedFile]);

  if (!moduleName) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select a module to view documentation.
      </Typography>
    );
  }

  if (loading) {
    return <CircularProgress size={24} />;
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    );
  }

  if (!details) {
    return (
      <Typography variant="body2" color="text.secondary">
        No details available for this module.
      </Typography>
    );
  }

  const tables = details.tables_used || [];
  const files = details.files || [];

  const handleSelectFile = (file) => {
    setSelectedFile(file);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h6" gutterBottom sx={{ wordBreak: "break-word" }}>
          {details.module}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {details.file_count} file{details.file_count === 1 ? "" : "s"} indexed in planner_file_map.
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Tables used
        </Typography>
        {tables.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No tables recorded for this module yet.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {tables.map((tbl) => (
              <Chip key={tbl} label={tbl} size="small" />
            ))}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Files in this module
        </Typography>

        {files.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No files recorded for this module.
          </Typography>
        ) : isMobile ? (
          <Box>
            {files.map((f) => (
              <MobileFileCard
                key={f.path}
                file={f}
                selected={selectedFile?.file_id === f.file_id}
                onSelect={handleSelectFile}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Path</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Tables</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((f) => (
                  <TableRow
                    key={f.path}
                    hover
                    selected={selectedFile?.file_id === f.file_id}
                    onClick={() => handleSelectFile(f)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-word" }}>
                      {f.path}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2">
                        {f.description || <em>(no description)</em>}
                      </Typography>
                    </TableCell>
                    <TableCell>{f.file_type || ""}</TableCell>
                    <TableCell>{(f.db_tables_used || []).join(", ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Functions
        </Typography>

        {!selectedFile && (
          <Typography variant="body2" color="text.secondary">
            Tap a file above to view its functions from planner_function_map.
          </Typography>
        )}

        {selectedFile && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" gutterBottom sx={{ wordBreak: "break-word" }}>
              Functions in <span style={{ fontFamily: "monospace" }}>{selectedFile.path}</span>
            </Typography>

            {functionsLoading && <CircularProgress size={20} />}

            {functionsError && (
              <Typography color="error" variant="body2">
                {functionsError}
              </Typography>
            )}

            {!functionsLoading && !functionsError && functions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No functions recorded for this file yet.
              </Typography>
            )}

            {!functionsLoading && !functionsError && functions.length > 0 && (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Function</TableCell>
                      <TableCell>Line</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {functions.map((fn) => (
                      <TableRow key={`${fn.function_name}-${fn.line_number}`}>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-word" }}>
                          {fn.function_name}
                        </TableCell>
                        <TableCell>{fn.line_number || ""}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{fn.description || ""}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
